import React, { ChangeEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Avatar, Box, Button, Card, CircularProgress, Divider, Switch, Tooltip, Typography, } from "@mui/material";
import { FilterList } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from "@mui/icons-material/Warning";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { SearchField, DrawerControl, StatusChip, Drawer, FilterSideBar, PaginationControls, FilterButton, FieldControl } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { RiskDetails } from "./RiskDetails";
import { ScroreCard } from "./ScroreCard";
import { RaiseRisk } from "./RaiseRisk";

import { useRiskRegister } from "../mutation";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { RiskHeader } from "./RiskDetailsOverview";
import { useFilterContext } from "../../../shared/context";
import { useActiveFilterLabels, useFilterReset } from "../../../shared/hooks";
import { useDisplayField } from "../../../hooks/useDisplayField";


interface RiskRegisterDataProps {
    "risk_id": string,
    "project_ref": string,
    "project_name": string,
    "project_id": number,
    "title": string,
    "risk_description": string,
    "review_date": string,
    "category_display_text": string,
    "cause_title": string,
    "status": string,
    "impact": string,
    "probability": string,
    "divident": number,
    "risk_ref": null,
    "score": number,
    "severity": string,
    "status_display_text": string,
    "color": string,
    "assessment_status": string,
    "impact_display": string,
    "probability_display": string
};

export const RiskRegister: React.FC<{ projectId: string | undefined }> = ({ projectId }) => {

    const { isAllowedToRaiseRisk } = useCapabilities();

    const [searchParams, setSearchParams] = useSearchParams();
    const { quickFilters, options: filterOptions, handleResetFilters } = useFilterContext();
    const { getFilterOptions } = useDisplayField([]);
    const { hasActiveFilters: hasActiveQuickFilters } = useActiveFilterLabels(["stakeholder", "project_status"]);


    const selected = searchParams.get('selected');
    const id = searchParams.get('id');
    const impactScore = searchParams.get('impact_score') || undefined;
    const probabilityScore = searchParams.get('probability_score') || undefined;

    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');
    const [assignedToMe, setAssignedToMe] = useState(false);
    const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
    const handleOpenFilter = () => setIsOpenFilter(true);
    const handleCloseFilter = () => setIsOpenFilter(false);
    const [statusFilter, setStatusFilter] = useState<string | null>(() => searchParams.get('status'));
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [statusDraft, setStatusDraft] = useState<string | null>(() => searchParams.get('status'));
    const [categoryDraft, setCategoryDraft] = useState<string | null>(null);

    const handleStatusFilterChange = (status: string) => {
        setStatusDraft(status || null);
    };

    const handleCategoryFilterChange = (category: string) => {
        setCategoryDraft(category || null);
    };

    const handleApplyExtraFilters = () => {
        setStatusFilter(statusDraft);
        setCategoryFilter(categoryDraft);
        setTimeout(() => refetch(), 0);
    };

    const handleResetExtraFilters = () => {
        setStatusDraft(null);
        setCategoryDraft(null);
        setStatusFilter(null);
        setCategoryFilter(null);
        setTimeout(() => refetch(), 0);
    };

    const handleAssignedToMeChange = () => {
        setAssignedToMe(!assignedToMe);
    };

    const hasActiveFilters = hasActiveQuickFilters || !!searchKey || assignedToMe || !!statusFilter || !!categoryFilter || !!impactScore || !!probabilityScore;

    const handleResetAllFilters = () => {
        setSearchDraft('');
        setSearchKey('');
        setAssignedToMe(false);
        setStatusFilter(null);
        setCategoryFilter(null);
        setStatusDraft(null);
        setCategoryDraft(null);
        handleResetFilters();

        const params = new URLSearchParams(searchParams);
        params.delete('impact_score');
        params.delete('probability_score');
        params.delete('status');
        setSearchParams(params, { replace: true });

        setTimeout(() => refetch(), 0);
    };

    const { isPending: isRiskRegisterPending, riskRegisterData, refetch, pagination, changePage, changeRowsPerPage } = useRiskRegister({
        projectId, searchKey, assignedToMe, quickFilters, status: statusFilter || undefined, category: categoryFilter || undefined, impactScore, probabilityScore
    });

    useFilterReset(() => changePage(1));

    const [raiseRisk, setRaiseRisk] = useState<{
        open: boolean,
        editStatus: boolean,
        riskId: string | null
    }>({
        open: false,
        editStatus: false,
        riskId: null
    });

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
    };

    const handleClear = () => {
        setSearchDraft('');
        setSearchKey('');
    };

    const handleSearch = () => {
        setSearchKey(searchDraft);
    };

    const openDrawer = (riskId: string) => {
        const params = new URLSearchParams(searchParams);
        params.set("selected", "true");
        params.set("id", riskId);
        setSearchParams(params, { replace: true });
    };

    const closeDrawer = () => {
        const params = new URLSearchParams(searchParams);
        params.delete("selected");
        params.delete("id");
        setSearchParams(params, { replace: true });
    };

    const riskExtraFilters = (
        <Box display="flex" flexDirection="column" gap={2}>
            <FieldControl
                keyName="riskStatus"
                label="Status"
                variant="select"
                value={statusDraft ?? ''}
                options={getFilterOptions('RISK_OVERVIEW_STATUS').map((domain: any) => ({
                    value: domain.key,
                    label: domain.display_text,
                }))}
                onChange={(_key, value) => handleStatusFilterChange(value)}
            />
            <FieldControl
                keyName="riskCategory"
                label="Category"
                variant="select"
                value={categoryDraft ?? ''}
                options={getFilterOptions('RISK_CATEGORY').map((domain: any) => ({
                    value: domain.key,
                    label: domain.display_text,
                }))}
                onChange={(_key, value) => handleCategoryFilterChange(value)}
            />
        </Box>
    );

    if (isRiskRegisterPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box p={2}>


            <Box display={'flex'} alignItems={'center'} gap={2} mb={2}>

                <SearchField
                    label="Search Risks"
                    searchTerm={searchDraft}
                    handleSearch={handleChangeSearch}
                    handleClearSearch={handleClear}
                    handleApplySearch={handleSearch}
                />


                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
                    <Button
                        startIcon={<RestartAltIcon />}
                        onClick={handleResetAllFilters}
                        disabled={!hasActiveFilters}
                    >
                        RESET FILTERS
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="subtitle2">Raised by Me :</Typography>
                        <Switch checked={assignedToMe} onChange={handleAssignedToMeChange} />
                    </Box>
                    <FilterButton hiddenFilters={["stakeholder", "project_status"]} onClick={handleOpenFilter} />
                    {
                        isAllowedToRaiseRisk && (
                            <Button startIcon={<AddIcon />} sx={{ ml: "auto" }} onClick={() => setRaiseRisk({ open: true, editStatus: false, riskId: null })}>
                                RAISE RISK
                            </Button>
                        )
                    }
                </Box>
            </Box>
            <Divider />
         <Box display={'flex'} flexDirection={'column'} gap={2} mt={2}>
                {
                    riskRegisterData.length === 0 ?
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            alignContent="center"
                            width="100%"
                            height="100%"
                            gap={2}
                            mt={5}
                        >
                            <Typography variant="body2" color="text.secondary">No risks found.</Typography>
                            <Typography variant="body2" color="text.secondary">Try adjusting your search or filter to find what you're looking for.</Typography>
                        </Box> :
                        riskRegisterData.map((value: RiskRegisterDataProps, key: number) => (
                            <Card sx={{ py: 1.5, px: 2 }} key={key} onClick={() => { openDrawer(value.risk_id) }}>
                                <Box display={'flex'} gap={3} alignItems={'center'}>
                                    <Avatar sx={{ bgcolor: value.status_display_text === 'Open' ? 'warning.main' : 'divider', borderRadius: 1 }}>
                                        <WarningIcon />
                                    </Avatar>
                                    <Box display={'flex'} flexDirection={'column'} gap={.8}>
                                        <Box display={'flex'} gap={2} alignItems={'center'}>
                                            <Typography variant="subtitle2">{value.title}</Typography>
                                            <StatusChip label={value.status_display_text} color={value.status_display_text === 'Open' ? 'amber' : 'default'} />
                                        </Box>
                                        <Box display={'flex'} gap={.5}>
                                            <Typography variant="caption" color="text.secondary">{value.project_name || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.project_ref || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.risk_ref || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.category_display_text || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(value?.review_date?.toString())}</Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="subtitle2" ml={'auto'}>{value.assessment_status}</Typography>
                                    <Typography variant="caption" color="text.secondary">Impact</Typography>
                                    <StatusChip label={String(value.impact_display || 'N/A')} color={(value.impact === 'high' || value.impact === 'very_high') ? "red" : value.impact === 'medium' ? "amber" : (value.impact === 'low' || value.impact === 'very_low') ? "green" : "default"} variant="filled" />
                                    <Typography variant="caption" color="text.secondary">Probability</Typography>
                                    <StatusChip label={String(value.probability_display || 'N/A')} color={(value.probability === 'high' || value.probability === 'very_high') ? "red" : value.probability === 'medium' ? "amber" : (value.probability === 'low' || value.probability === 'very_low') ? "green" : "default"} variant="filled" />
                                    <Typography variant="caption" color="text.secondary">Score</Typography>
                                    <ScroreCard score={value.score} />
                                </Box>
                            </Card>
                        ))
                }
                <PaginationControls
                    total={pagination?.total}
                    rowsPerPage={pagination?.limit}
                    currentPage={pagination?.page}
                    handleRowsPerPage={changeRowsPerPage}
                    handlePageChange={changePage}
                />
            </Box>

            <Drawer
                isOpen={!!selected}
                header={
                    !!id &&
                    <RiskHeader
                        riskId={String(id)}
                        handleClose={() => closeDrawer()}
                    />
                }
            >
                <RiskDetails
                    riskId={String(id)}
                    onEdit={() => {
                        setRaiseRisk({ open: true, editStatus: true, riskId: String(id) });
                        closeDrawer();
                    }}
                    refetch={refetch}
                />
            </Drawer>

            <DrawerControl
                width="35%"
                title="Add/Edit Risk"
                subTitle={""}
                isOpen={raiseRisk.open}
                handleClose={() => { setRaiseRisk({ open: false, editStatus: false, riskId: null }) }}
                children={
                    <RaiseRisk
                        riskId={raiseRisk.riskId}
                        editStatus={raiseRisk.editStatus}
                        projectId={projectId}
                        onCancel={() => setRaiseRisk({ open: false, editStatus: false, riskId: null })}
                        refetch={refetch}
                    />
                }
            />
            <DrawerControl
                title="Filter Records"
                subTitle="Please apply following filters to refine results"
                isOpen={isOpenFilter}
                handleClose={handleCloseFilter}
            >
                <FilterSideBar
                    hiddenFilters={["stakeholder", "project_status"]}
                    extraFilters={riskExtraFilters}
                    onApplyExtra={handleApplyExtraFilters}
                    onResetExtra={handleResetExtraFilters}
                    onClose={handleCloseFilter}
                />
            </DrawerControl>

        </Box>
    );
};